import { useEffect, useId, useRef, useState } from "react";
import type { UseFormRegisterReturn } from "react-hook-form";
import { Plus } from "lucide-react";
import { INPUT_CLASS } from "../../lib/forms/inputClass";
import { FOCUS_RING } from "../../lib/focusRing";
import { cn } from "../../lib/cn";
import { Field } from "../ui/Field";
import { LEGAL_NAME_HINT, LEGAL_NAME_LABEL } from "./childNameCopy";

/**
 * The legal-name input (BRIEF §5.2), collapsed behind "Add legal name (optional)". It opens
 * by itself when the child already has one (edit form) or when the field has a
 * validation error. Once open it stays open: collapsing it again would hide a
 * value that is still going to be saved. To remove a legal name, clear the box.
 */
export function LegalNameField({
  id,
  registration,
  error,
  hasValue,
}: {
  id: string;
  registration: UseFormRegisterReturn;
  error?: string;
  hasValue: boolean;
}) {
  const [open, setOpen] = useState(hasValue);
  const openedByClick = useRef(false);
  const regionId = useId();
  const isOpen = open || Boolean(error);

  useEffect(() => {
    if (isOpen && openedByClick.current) {
      openedByClick.current = false;
      document.getElementById(id)?.focus();
    }
  }, [isOpen, id]);

  if (!isOpen) {
    return (
      <button
        type="button"
        aria-expanded={false}
        aria-controls={regionId}
        onClick={() => {
          openedByClick.current = true;
          setOpen(true);
        }}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full py-1 text-sm font-medium text-sky-700 hover:underline",
          FOCUS_RING,
        )}
      >
        <Plus className="h-4 w-4" aria-hidden />
        Add legal name (optional)
      </button>
    );
  }

  return (
    <div id={regionId}>
      <Field label={LEGAL_NAME_LABEL} htmlFor={id} error={error} hint={LEGAL_NAME_HINT}>
        <input
          id={id}
          type="text"
          autoComplete="off"
          {...registration}
          className={INPUT_CLASS}
          placeholder="As it appears on official records"
        />
      </Field>
    </div>
  );
}
